import { classifyImageSource } from './classifyImageSource';

interface HastNode {
    type: string;
    tagName?: string;
    value?: string;
    properties?: Record<string, unknown>;
    children?: HastNode[];
}

export interface BlockExternalImagesOptions {
    approvedHosts: readonly string[];
    label: string;
}

const MAX_DISPLAY_LENGTH = 200;

const truncate = (value: string): string =>
    value.length > MAX_DISPLAY_LENGTH ? `${value.slice(0, MAX_DISPLAY_LENGTH)}…` : value;

const toPlaceholder = (node: HastNode, label: string): HastNode => {
    const alt = typeof node.properties?.alt === 'string' ? node.properties.alt.trim() : '';
    const src = typeof node.properties?.src === 'string' ? node.properties.src.trim() : '';
    const value = [label, alt, truncate(src)].filter((part) => part.length > 0).join(' ');
    return { type: 'text', value };
};

/** Replaces every `img` that is not an allowed source with a plain text node, before any element can load. */
export const rehypeBlockExternalImages = ({ approvedHosts, label }: BlockExternalImagesOptions) => {
    const visit = (parent: HastNode): void => {
        const children = parent.children;
        if (!children) {
            return;
        }
        for (let index = 0; index < children.length; index++) {
            const child = children[index];
            // A <source srcset> inside <picture> overrides the <img> source, so it must never survive.
            if (child.type === 'element' && child.tagName === 'source') {
                children.splice(index, 1);
                index--;
            } else if (child.type === 'element' && child.tagName === 'img') {
                const src = typeof child.properties?.src === 'string' ? child.properties.src : undefined;
                if (classifyImageSource(src, approvedHosts).kind === 'block') {
                    children[index] = toPlaceholder(child, label);
                }
            } else {
                visit(child);
            }
        }
    };

    return (tree: HastNode): void => visit(tree);
};
